import React, { useState } from 'react'
import * as FileSaver from 'file-saver'
import * as XLSX from 'xlsx'
import { useContextStatistics } from '../../../contexts/statisticsContext'
import useStatisticsServices from '../../../services/statistics'
import useError from '../../../hooks/useError'
import { getFilters } from '../../../utils/statistics'
import { formatDate } from '../../../utils/date'

// must not exceed STATS_MAX_PAGE_SIZE on the server
const EXPORT_PAGE_SIZE = 300

const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
const fileExtension = '.xlsx'

const COLUMNS = [
    { title: 'Date', width: 12, getValue: row => formatDate(new Date(row.date), '%d %b %Y') },
    { title: 'Created', width: 12, getValue: row => formatDate(new Date(row.created), '%d %b %Y') },
    { title: 'Client', width: 20, getValue: row => row.clientname },
    { title: 'Project', width: 25, getValue: row => row.projectname },
    { title: 'Job', width: 20, getValue: row => row.jobname },
    { title: 'Zone', width: 20, getValue: row => row.zonename },
    { title: 'System', width: 20, getValue: row => row.systemname },
    { title: 'Lead', width: 20, getValue: row => row.leadname },
    { title: 'Activity', width: 20, getValue: row => row.activityname },
    { title: 'Revision', width: 15, getValue: row => row.revisionname },
    { title: 'User', width: 20, getValue: row => row.name },
    { title: 'Hours', width: 8, getValue: row => row.hours },
]

const getFileName = (selectedRange) => {
    if (!selectedRange.length) {
        return `Statistics_${formatDate(new Date(), '%Y-%m-%d')}`
    }

    const from = new Date(Math.min(...selectedRange.map(r => r.from.getTime())))
    const to = new Date(Math.max(...selectedRange.map(r => r.to.getTime())))

    return `Statistics_${formatDate(from, '%Y-%m-%d')}_to_${formatDate(to, '%Y-%m-%d')}`
}

const ExportResults = () => {
    const { statisticsState } = useContextStatistics()
    const { resultsTotalCount, filtersChanged } = statisticsState
    const { getStatistics } = useStatisticsServices()
    const { setErrorMessage } = useError()
    const [isExporting, setIsExporting] = useState(false)

    const fetchAllResults = async (filters) => {
        const pageCount = Math.ceil(resultsTotalCount / EXPORT_PAGE_SIZE)
        let results = []

        for (let page = 1; page <= pageCount; page++) {
            const response = await getStatistics(filters, page, EXPORT_PAGE_SIZE, false)

            if (response.error) {
                setErrorMessage(response)
                return null
            }

            results = results.concat(response.statistics)
        }

        return results
    }

    const saveExcelFile = (results, fileName) => {
        const totalHours = results.reduce((total, row) => total + row.hours, 0)
        const sheetData = [
            COLUMNS.map(c => c.title),
            ...results.map(row => COLUMNS.map(c => c.getValue(row) ?? '')),
            [...Array(COLUMNS.length - 2).fill(''), 'Total hours:', totalHours]
        ]

        const ws = XLSX.utils.aoa_to_sheet(sheetData)
        ws['!cols'] = COLUMNS.map(c => ({ wch: c.width }))
        const wb = { Sheets: { Statistics: ws }, SheetNames: ['Statistics'] }
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
        const data = new Blob([excelBuffer], { type: fileType })
        FileSaver.saveAs(data, fileName + fileExtension)
    }

    const handleExport = async () => {
        setIsExporting(true)

        try {
            const filters = getFilters(statisticsState)
            const results = await fetchAllResults(filters)

            if (results) {
                saveExcelFile(results, getFileName(filters.selectedRange))
            }
        } catch (error) {
            setErrorMessage({ error: 'Export to Excel failed.' })
        } finally {
            setIsExporting(false)
        }
    }

    return (
        <button
            onClick={handleExport}
            disabled={isExporting || filtersChanged || !resultsTotalCount}
            title={filtersChanged ? 'Filters changed - press Display to refresh the results before exporting' : ''}
        >
            {isExporting ? 'Exporting...' : 'Export to Excel'}
        </button>
    )
}

export default ExportResults
